import React, { useCallback, useEffect, useState } from 'react'
import { ArrowRight, X } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { SuggestionsPanel } from '../types'
import { useStartZephChat } from '../hooks/useStartZephChat'

interface SuggestionCardsProps {
  panel: SuggestionsPanel
}

// If the same cards appear every morning and get ignored, the row goes invisible.
// A dismissal therefore has to stick — but only for a while, since the underlying
// work usually still needs doing. Per-browser is the right scope for a "not now":
// it is a display preference, not a decision worth syncing.
const DISMISS_KEY = 'zeph-dismissed-suggestions'
const DISMISS_TTL_MS = 3 * 24 * 60 * 60 * 1000

type DismissMap = Record<string, number>

function readDismissed(): DismissMap {
  try {
    const raw = localStorage.getItem(DISMISS_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const now = Date.now()
    return Object.fromEntries(
      Object.entries(parsed as DismissMap).filter(([, at]) => typeof at === 'number' && now - at < DISMISS_TTL_MS),
    )
  } catch {
    return {}
  }
}

function writeDismissed(map: DismissMap): void {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify(map))
  } catch {
    /* private mode, blocked storage — dismissal just won't persist */
  }
}

const SuggestionCards: React.FC<SuggestionCardsProps> = ({ panel }) => {
  const { currentTheme: t } = useTheme()
  const startZephChat = useStartZephChat()
  const [dismissed, setDismissed] = useState<DismissMap>({})

  useEffect(() => {
    setDismissed(readDismissed())
  }, [])

  const dismiss = useCallback((key: string) => {
    setDismissed((current) => {
      const next = { ...current, [key]: Date.now() }
      writeDismissed(next)
      return next
    })
  }, [])

  const visible = panel.items.filter((item) => !(item.key in dismissed))
  if (visible.length === 0) return null

  return (
    <section aria-label="Work Zeph could take on next">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {visible.map((item) => (
          <div
            key={item.key}
            className="group flex flex-col gap-2 rounded-xl border p-3.5 min-w-0 transition-all duration-200 hover:-translate-y-0.5"
            style={{ backgroundColor: t.secondary, borderColor: t.border, boxShadow: t.cardShadow }}
          >
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: t.accent }}>
              {item.category}
            </span>
            <p className="text-[13px] leading-snug flex-1" style={{ color: t.text }}>
              {item.text}
            </p>
            <div className="flex items-center justify-between gap-2">
              {/* Clicking never executes the work — it opens a chat seeded with it,
                  which keeps the user in the approval loop. */}
              <button
                type="button"
                onClick={() =>
                  startZephChat({
                    seedKey: `suggestion:${item.key}`,
                    title: item.text,
                    briefing: item.seedPrompt,
                  })
                }
                className="inline-flex items-center gap-1 text-[11.5px] font-semibold transition-opacity hover:opacity-80"
                style={{ color: t.accent }}
              >
                Start
                <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
              <button
                type="button"
                onClick={() => dismiss(item.key)}
                aria-label={`Dismiss suggestion: ${item.text}`}
                title="Not now"
                // Visible but quiet by default rather than hover-only: on a touch
                // screen there is no hover, and an unreachable dismiss is the whole
                // reason this row would turn into wallpaper.
                className="rounded-md p-0.5 transition-opacity opacity-40 hover:opacity-100 focus-visible:opacity-100"
                style={{ color: t.mutedText }}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

export default React.memo(SuggestionCards)
